/**
 * The two voltages a string length is chosen from, for display beside the
 * selected module.
 *
 * PVsyst puts these on the module panel itself -- "Sizing voltages: Vmpp (60°C)
 * 32.1 V, Voc (-10°C) 50.9 V" -- because they are what an engineer checks
 * before anything else, and reading them off a datasheet by hand is exactly the
 * arithmetic a tool should have already done.
 *
 * MIRRORS pv_core/string_limits.py and the voltage arithmetic in
 * pv_core/electrical_check.py. If the rule changes there, change it here: a
 * panel that disagrees with the check on the next tab is worse than a panel
 * that shows nothing. In particular the Vmp coefficient is NOT simply read --
 * it falls back to the power coefficient, and the caller is told which was
 * used, because "approximated" and "from the datasheet" are different claims.
 */

export const STC_CELL_C = 25.0;

// Key spellings, in the order the backend tries them.
//
// The Vmp coefficient is split by the UNIT each spelling carries. Sandia's
// Bvmpo is VOLTS per degree -- the same convention as Bvoco beside it, and as
// beta_oc below -- while gamma_vmp is a percent. They were one list, all read
// as percent, which understates the temperature response by a factor of
// 100/Vmp (2.2x on a 45 V module) and in the dangerous direction: Vmp at 70 °C
// comes out too high, so a string too short for the MPPT floor looks fine.
const VMP_COEFF_V_PER_C_KEYS = ["Bvmpo", "Bvmp", "beta_vmp"];
const VMP_COEFF_PCT_KEYS = ["gamma_vmp"];
const POWER_COEFF_KEYS = ["gamma_r", "gamma_pdc", "gamma_pmp"];
const VOC_COEFF_KEYS = ["beta_oc", "Bvoco", "beta_voc"];
const VOC_KEYS = ["V_oc_ref", "Voc_ref", "Voc0", "Voco", "Voc"];
const VMP_KEYS = ["V_mp_ref", "Vmp_ref", "Vmp0", "Vmpo", "Vmp"];

function num(value) {
  if (value === null || value === undefined || value === "") return null;
  const out = Number(String(value).replace(",", "."));
  return Number.isFinite(out) ? out : null;
}

function pick(chars, keys) {
  if (!chars) return null;
  for (const key of keys) {
    const found = num(chars[key]);
    if (found !== null) return found;
  }
  return null;
}

// What a Vmp coefficient can physically be, in %/°C -- wide enough for HJT at
// the shallow end and thin film at the steep one. A backstop against a record
// whose spelling lies about its unit, not a second opinion on a real datasheet:
// a value outside it is still used, and the source says so.
const PLAUSIBLE_VMP_PCT = [0.15, 0.55];

const plausible = (pct) =>
  pct !== null && Math.abs(pct) >= PLAUSIBLE_VMP_PCT[0] && Math.abs(pct) <= PLAUSIBLE_VMP_PCT[1];

/**
 * The Vmp temperature coefficient in %/°C, and where it came from.
 *
 * Mirrors string_limits.vmp_temperature_coefficient exactly, including the
 * order of the decisions:
 *
 *   gamma_vmp                already a percent, taken as it stands
 *   Bvmpo / Bvmp / beta_vmp  volts per degree, converted with Vmp at STC
 *   gamma_r / gamma_pdc / …  the power coefficient standing in, and said so
 *
 * Converting the absolute form needs Vmp. Without it the volts CANNOT be made
 * into a percent, and rather than pass one off as the other this falls through
 * to the power coefficient -- a stated approximation instead of a silent unit
 * error. The physical check then catches a record that stored a percent under
 * an absolute key, which is how several fixtures were written.
 */
export function vmpTemperatureCoefficient(chars, vmpStc = null) {
  const vmp = vmpStc !== null ? num(vmpStc) : pick(chars, VMP_KEYS);

  const relative = pick(chars, VMP_COEFF_PCT_KEYS);
  if (relative !== null && relative !== 0) {
    return { pctPerC: relative, source: "datasheet (relative, %/\u00b0C)" };
  }

  const absolute = pick(chars, VMP_COEFF_V_PER_C_KEYS);
  if (absolute !== null && absolute !== 0) {
    const converted = vmp ? (100.0 * absolute) / vmp : null;
    if (plausible(converted)) {
      return { pctPerC: converted, source: "datasheet (absolute V/\u00b0C, converted)" };
    }
    if (converted !== null && plausible(absolute)) {
      // Impossible as volts, sound as a percent. Only ever asked once there IS
      // a conversion to compare against -- with no Vmp, "looks like a plausible
      // percent" is not evidence, it is the original bug.
      return {
        pctPerC: absolute,
        source: "datasheet (stored under an absolute key but read as %/\u00b0C)",
      };
    }
    if (converted !== null) {
      return {
        pctPerC: converted,
        source: "datasheet (absolute V/\u00b0C, converted; outside the usual range)",
      };
    }
    // Volts with no Vmp to divide by: unconvertible, so fall through.
  }

  const power = pick(chars, POWER_COEFF_KEYS);
  if (power !== null && power !== 0) {
    const pct = Math.abs(power) < 0.05 ? power * 100.0 : power;
    return { pctPerC: pct, source: "approximated from the power coefficient" };
  }
  return null;
}

/**
 * Voc at the coldest cell and Vmpp at the hottest, for one module.
 *
 * Returns null for a figure that cannot be computed rather than a zero: a
 * missing coefficient must read as "not known", never as "no variation with
 * temperature", which is the more dangerous of the two on a string check.
 */
export function sizingVoltages(chars, { minCellC = -10, maxCellC = 70 } = {}) {
  const coldC = num(minCellC) ?? -10;
  const hotC = num(maxCellC) ?? 70;

  const voc = pick(chars, VOC_KEYS);
  const vmp = pick(chars, VMP_KEYS);
  const betaOc = pick(chars, VOC_COEFF_KEYS);          // V/°C
  // Vmp is handed over so the absolute form is converted against the SAME Vmp
  // the hot voltage is computed from.
  const coeff = vmpTemperatureCoefficient(chars, vmp);

  const vocCold =
    voc !== null && betaOc !== null ? voc + betaOc * (coldC - STC_CELL_C) : null;
  const vmpHot =
    vmp !== null && coeff ? vmp * (1 + (coeff.pctPerC / 100) * (hotC - STC_CELL_C)) : null;

  return {
    coldC,
    hotC,
    vocStc: voc,
    vmpStc: vmp,
    vocCold,
    vmpHot,
    coeffSource: coeff ? coeff.source : null,
  };
}
